import { useState } from "react";
import { toast } from "sonner";
import { Download, Check, Loader2, WifiOff } from "lucide-react";
import { isBookSaved, saveBookOffline, removeBookOffline, useOnline } from "@/lib/offline";

export const OfflineButton = ({ slug, manifest }) => {
  const [saved, setSaved] = useState(() => isBookSaved(slug));
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const online = useOnline();

  const save = async () => {
    if (!navigator.onLine) { toast.error("Connect to the internet to save this book for offline"); return; }
    setBusy(true); setPct(0);
    try {
      await saveBookOffline(slug, manifest.title, manifest, setPct);
      setSaved(true);
      toast.success("Saved for offline reading");
    } catch (e) {
      toast.error(e.message || "Couldn't save for offline");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    await removeBookOffline(slug);
    setSaved(false);
    toast.success("Removed offline copy");
  };

  if (!online && saved) {
    return (
      <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-[#E8FBF0] px-2.5 py-1 text-[11px] font-semibold text-[#2E8B57]" data-testid="offline-ready-badge">
        <WifiOff className="h-3.5 w-3.5" />Offline
      </span>
    );
  }

  if (busy) {
    return (
      <span className="flex shrink-0 items-center gap-1.5 rounded-full neu-inset px-3 py-1.5 text-[11px] font-medium text-[#334155]" data-testid="offline-saving">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-[#3D3DF0]" />Saving {pct}%
      </span>
    );
  }

  if (saved) {
    return (
      <button onClick={remove} title="Available offline — tap to remove" data-testid="offline-remove-btn"
        className="flex shrink-0 items-center gap-1.5 rounded-full bg-[#E8FBF0] px-3 py-1.5 text-[11px] font-semibold text-[#2E8B57] transition-colors hover:bg-[#d7f5e5]">
        <Check className="h-3.5 w-3.5" />Offline ready
      </button>
    );
  }

  return (
    <button onClick={save} title={online ? "Save this book to read offline" : "Connect to the internet to save offline"} data-testid="offline-save-btn"
      className="flex shrink-0 items-center gap-1.5 rounded-full neu-btn px-3 py-1.5 text-[11px] font-medium text-[#334155] transition-colors hover:text-[#0F172A]">
      <Download className="h-3.5 w-3.5 text-[#3D3DF0]" />Save offline
    </button>
  );
};
